import { SHORTS_FEED_SURFACE_CONTRACT } from '~/composables/shorts/shortsSurfaceContract';
import { useFeedSession } from '~/composables/shorts/useFeedSession';
import { defineStore } from 'pinia';

/**
 * Instância global da lista do feed de shorts.
 *
 * A store não implementa mais nada: itens, dedupe, paginação, contadores e
 * controle de corrida vivem em `useFeedSession`. O que ela dá é o escopo — um
 * único dono da lista do feed, compartilhado entre o reprodutor, o botão do
 * header e a sidebar de explorar, sem singleton de módulo que vazaria entre
 * requests de SSR.
 */
export const useShortsFeedStore = defineStore('shortsFeed', () => {
	const session = useFeedSession({
		perPage: SHORTS_FEED_SURFACE_CONTRACT.perPage,
		fetchOptions: SHORTS_FEED_SURFACE_CONTRACT.fetchOptions
	});
	const isSeededFromCarousel = ref(false);

	async function load(...args: Parameters<typeof session.load>): Promise<void> {
		isSeededFromCarousel.value = false;
		await session.load(...args);
	}

	function seed(...args: Parameters<typeof session.seed>): void {
		isSeededFromCarousel.value = true;
		session.seed(...args);
	}

	function reset(...args: Parameters<typeof session.reset>): void {
		isSeededFromCarousel.value = false;
		session.reset(...args);
	}

	return {
		videos: session.items,
		isLoading: session.isLoading,
		isLoadingMore: session.isLoadingMore,
		isPreloaded: session.isLoaded,
		hasMore: session.hasMore,
		currentPage: session.page,
		feedPerPage: session.perPage,
		meta: session.meta,
		activeFilters: session.filters,
		allWatched: session.allWatched,
		totalAvailable: session.totalAvailable,
		recommendationCountVideos: session.recommendationCountVideos,
		recommendationCountPhotos: session.recommendationCountPhotos,
		isSeededFromCarousel,

		load,
		loadMore: session.loadMore,
		seed,
		snapshot: session.snapshot,
		reset,
		stop: session.stop,
		resume: session.resume,
		remainingAfter: session.remainingAfter
	};
});
